/**
 * Pick a receipt out of the job's CompanyCam photographs.
 *
 * Samuel reads receipts onto material orders from his laptop by choosing them
 * in the file picker, where the CompanyCam browser extension lists his
 * project photographs. His Android has no such extension, so this shows the
 * same photographs inside Roof Board: that job's project only, newest first
 * (Jonathon, 2026-10-03). The one tapped is handed back as a file and read
 * exactly as an upload would be. Returns' credit memos get the same picker
 * (Jonathon, 2026-10-04: "it can go there too").
 */

import { useEffect, useState } from "react";

import { ErrorNote, Modal } from "../../components/ui";
import {
  type CompanycamPhoto,
  fetchJobPhoto,
  listJobPhotos,
} from "../../lib/companycam";
import { fmtDateTime } from "../../lib/format";
import { t } from "../../i18n";

export function CompanyCamPhotoPicker({
  jobId,
  what = "receipt",
  onPick,
  onClose,
}: {
  jobId: string;
  /** What is being picked, as the title and hint name it. */
  what?: "receipt" | "credit memo";
  onPick: (file: File) => void;
  onClose: () => void;
}) {
  const [photos, setPhotos] = useState<CompanycamPhoto[]>([]);
  const [linked, setLinked] = useState(true);
  const [more, setMore] = useState(false);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [fetching, setFetching] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    setLoading(true);
    listJobPhotos(jobId, page)
      .then((result) => {
        if (!live) return;
        setLinked(result.linked);
        setMore(result.more);
        // A later page adds to what is shown; it never replaces it.
        setPhotos((was) => {
          const seen = new Set(was.map((p) => p.id));
          return [...was, ...result.photos.filter((p) => !seen.has(p.id))];
        });
      })
      .catch((e: unknown) => {
        if (live) setError(e instanceof Error ? e.message : String(e));
      })
      .finally(() => {
        if (live) setLoading(false);
      });
    return () => {
      live = false;
    };
  }, [jobId, page]);

  async function choose(photo: CompanycamPhoto) {
    setFetching(photo.id);
    setError(null);
    try {
      const file = await fetchJobPhoto(jobId, photo);
      onPick(file);
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setFetching(null);
    }
  }

  return (
    <Modal
      title={
        what === "receipt"
          ? t("Receipt from CompanyCam")
          : t("Credit memo from CompanyCam")
      }
      subtitle={t("This job's photos, newest first. Tap {what} to read it.", {
        what: what === "receipt" ? t("the receipt") : t("the credit memo"),
      })}
      onClose={onClose}
      actions={
        <button className="btn-ghost" onClick={onClose}>
          {t("Cancel")}
        </button>
      }
    >
      <ErrorNote error={error} />
      {!linked && (
        <div className="na-hint">
          {t(
            "This job has no CompanyCam project yet, so there are no photos to pick from.",
          )}
        </div>
      )}
      {linked && !loading && photos.length === 0 && !error && (
        <div className="na-hint">
          {t("No photos on this job's CompanyCam project yet.")}
        </div>
      )}
      <div className="cc-picker">
        {photos.map((photo) => (
          <button
            key={photo.id}
            type="button"
            className="cc-picker-photo"
            disabled={fetching !== null}
            onClick={() => void choose(photo)}
            aria-label={t("Photo taken {when}", {
              when: fmtDateTime(photo.captured_at),
            })}
          >
            <img src={photo.thumbnail} alt="" loading="lazy" />
            <span>
              {fetching === photo.id
                ? t("Getting it…")
                : fmtDateTime(photo.captured_at)}
            </span>
          </button>
        ))}
      </div>
      {loading && <div className="na-hint">{t("Loading photos…")}</div>}
      {more && !loading && (
        <button className="addrow ghost" onClick={() => setPage((p) => p + 1)}>
          {t("Show older photos")}
        </button>
      )}
    </Modal>
  );
}
